import { TFunction } from 'i18next';
import { groupBy } from 'lodash';
import { MINUTES_PLAYED_COLUMN, SELECTION_COLUMN_STICKY_WIDTH_REM } from '@/consts/dataLocker/table';
import { LINK_SORTING_MAP } from '@/consts/ordering';
import { NestedObject } from '@/types/object';
import { ColumnGroup, ColumnOption, ColumnSubGroup, DataTableColumn } from '@/types/table';
import { snakeToCamel } from './queries';
import { getTranslationColumns } from './translations';

export const translateValue = (key: string, value: NestedObject[string], t?: TFunction) => {
  if (!value) return '';
  if (!t || typeof value !== 'string') return value;

  return t(`fields.${key}.${snakeToCamel(value.toLowerCase())}`);
};

export const translateHyphenatedValue = (key: string, value: NestedObject[string], t?: TFunction) => {
  if (!value) return '';
  if (!t || typeof value !== 'string') return value;
  const splitVals = value.split(' - ');
  const translatedVals = splitVals
    .map(valueElement => valueElement.replaceAll(' ', '_'))
    .map(valueElement => translateValue(key, valueElement, t));
  return translatedVals.join(' - ');
};

export const getHighlightedColumnIndex = (sortedColumnIndex: number, hasAdditionalColumn: boolean) => {
  if (sortedColumnIndex === -1) {
    return undefined;
  }
  // If there's an additional column (e.g. checkbox for selection), we need to offset the index by 1
  return sortedColumnIndex + 1 + Number(hasAdditionalColumn);
};

// get the data type to pass into a table cell for correct alignment
export const getDataType = (formatRule: string | undefined) => {
  if (!formatRule || formatRule.includes('string') || ['link', 'date'].includes(formatRule)) return 'string';

  return 'number';
};

export const isLowerBetter = (formatRule = '') => ['string', 'string-translate', 'link'].includes(formatRule);

export const getValidColumnKey = (tableColumnKey: string) => LINK_SORTING_MAP[tableColumnKey] || tableColumnKey;

export const getFormatRule = (tableColumnKey: string, tableColumns?: DataTableColumn[]) =>
  tableColumns?.find(({ tableColumn }) => getValidColumnKey(tableColumn.key) === getValidColumnKey(tableColumnKey))
    ?.tableColumn.formatRule;

const toHeader = (name: string) => ({ translationKey: snakeToCamel(name), translationNs: 'columnGroups' });

const toOption = ({ tableColumn: { key, isCore }, is360, isPhysical }: DataTableColumn): ColumnOption => ({
  value: key,
  label: getTranslationColumns(snakeToCamel(key)),
  is360,
  isCore,
  isPhysical,
});

/**
 * Group and format columns into a uniform Category -> Sub Category structure.
 */
export const groupTableColumns = (tableColumns: DataTableColumn[]): ColumnGroup[] => {
  const byTopLevel = groupBy(tableColumns, ({ categoryPath = [] }) => (categoryPath[1] ?? categoryPath[0])?.name ?? '');

  return Object.entries(byTopLevel).map(([topLevelName, columns]) => {
    const bySubCategory = groupBy(columns, ({ categoryPath = [] }) => categoryPath[2]?.name ?? '');
    const subGroups: ColumnSubGroup[] = Object.entries(bySubCategory).map(([subCategoryName, subColumns]) => ({
      header: toHeader(subCategoryName),
      options: subColumns.map(toOption),
    }));

    return { header: toHeader(topLevelName), options: subGroups };
  });
};

export const isMetricColumn = (columnKey: string) =>
  columnKey.startsWith('aggregates.') && columnKey !== MINUTES_PLAYED_COLUMN;

// Only event attribute columns get wrappable headers
export const isEventAttributeColumn = (columnKey: string) => columnKey.startsWith('event.attributes.');

// Move the locked column to index 0 if a locked key is provided. Idempotent;
// also dedupes the locked key if it appears multiple times in the input.
export const withLockedColumn = (columns: string[], lockedKey?: string) => {
  if (!lockedKey) return columns;
  return [lockedKey, ...columns.filter(column => column !== lockedKey)];
};

// Kitbag's Table accepts stickyColumns as `{ index, width }[]` with `index` 1-based (CSS nth-child).
// The locked column is always at visible index 0 by construction (withLockedColumn enforces it), so
// it lands at CSS column 1 — or 2 when the row-selection checkbox is rendered ahead of it.
//
// When `hasSelectionColumn` is true the checkbox column is pinned alongside the locked column.
// Pinning only the locked column in that case would leave the checkbox scrolling out of view while
// its row is still pinned, which looks broken.
export const getLockedStickyColumns = (hasSelectionColumn: boolean) => {
  const lockedColumn = { index: hasSelectionColumn ? 2 : 1, width: 0 };
  return hasSelectionColumn ? [{ index: 1, width: SELECTION_COLUMN_STICKY_WIDTH_REM }, lockedColumn] : [lockedColumn];
};
